import { useMemo, useRef, useLayoutEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { useDashboardConfig } from '../context/DashboardContext';

function formatValue(value, unit) {
  if (value === null || value === undefined) return 'N/A';
  if (unit === 'percent') return `${value.toFixed(1)}%`;
  if (unit === 'dollars') return `$${Math.round(value).toLocaleString()}`;
  if (unit === 'index') return value.toFixed(2);
  return value.toLocaleString();
}

function formatDelta(delta, unit) {
  if (delta === null || isNaN(delta)) return null;
  const sign = delta >= 0 ? '+' : '';
  if (unit === 'percent') return `${sign}${delta.toFixed(1)} pp`;
  if (unit === 'dollars') return `${sign}$${Math.round(delta).toLocaleString()}`;
  if (unit === 'index') return `${sign}${delta.toFixed(2)}`;
  return `${sign}${Math.round(delta).toLocaleString()}`;
}

function getYearValue(trendArr, year) {
  if (!trendArr || !year) return null;
  return trendArr.find(p => p.year === year)?.value ?? null;
}

function DeltaBadge({ value, reference, unit }) {
  if (value === null || reference === null) return null;
  const delta = value - reference;
  const label = formatDelta(delta, unit);
  if (!label) return null;
  return (
    <span className={`scorecard-delta${delta > 0 ? ' scorecard-delta--pos' : delta < 0 ? ' scorecard-delta--neg' : ''}`}>
      {label}
    </span>
  );
}

function IndicatorSparkline({ trend, stateTrend, comparisonTrend, comparisonName, selectedYear, onYearSelect, primaryColor, accentColor, unit, fontBody }) {
  if (!trend || trend.length < 2) {
    return <div className="scorecard-sparkline-empty">no trend data</div>;
  }

  const stateByYear = new Map((stateTrend || []).map(p => [p.year, p.value]));
  const compByYear = new Map((comparisonTrend || []).map(p => [p.year, p.value]));
  const hasComparison = (comparisonTrend || []).length > 0;

  const data = [...trend]
    .sort((a, b) => a.year - b.year)
    .map(p => ({
      ...p,
      stateValue: stateByYear.get(p.year) ?? null,
      comparisonValue: compByYear.get(p.year) ?? null,
    }));

  function handleClick(chartData) {
    const year = chartData?.activePayload?.[0]?.payload?.year;
    if (year) onYearSelect(year);
  }

  function tooltipLabel(name) {
    if (name === 'value') return 'Home county';
    if (name === 'stateValue') return 'State avg';
    if (name === 'comparisonValue') return comparisonName ?? 'Comparison';
    return name;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={data}
        margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#eeeeee" vertical={false} />
        <XAxis
          dataKey="year"
          tick={{ fontSize: 10, fontFamily: fontBody, fill: '#999' }}
          tickLine={false}
          axisLine={false}
          interval={0}
        />
        <YAxis hide domain={['auto', 'auto']} />
        <Tooltip
          formatter={(v, name) => [formatValue(v, unit), tooltipLabel(name)]}
          labelFormatter={y => `${y}`}
          contentStyle={{ fontSize: '11px', padding: '4px 8px', borderRadius: '4px' }}
        />
        {selectedYear && (
          <ReferenceLine
            x={selectedYear}
            stroke="rgba(0,0,0,0.18)"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}
        <Line
          type="monotone"
          dataKey="stateValue"
          stroke="#aaa"
          strokeWidth={1.5}
          strokeDasharray="4 3"
          isAnimationActive={false}
          dot={false}
          activeDot={{ r: 4, stroke: '#fff', strokeWidth: 1 }}
        />
        {hasComparison && (
          <Line
            type="monotone"
            dataKey="comparisonValue"
            stroke="#2e7d72"
            strokeWidth={1.5}
            strokeDasharray="6 3"
            isAnimationActive={false}
            dot={false}
            activeDot={{ r: 4, stroke: '#fff', strokeWidth: 1 }}
          />
        )}
        <Line
          type="monotone"
          dataKey="value"
          stroke={primaryColor}
          strokeWidth={2}
          isAnimationActive={false}
          dot={(props) => {
            const { cx, cy, payload } = props;
            const isSelected = payload.year === selectedYear;
            return (
              <circle
                key={`dot-${payload.year}`}
                cx={cx}
                cy={cy}
                r={isSelected ? 6 : 3}
                fill={isSelected ? accentColor : primaryColor}
                stroke="#fff"
                strokeWidth={2}
              />
            );
          }}
          activeDot={{ r: 7, stroke: '#fff', strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function Scorecard({
  allData, selectedTract, enabledIndicators,
  indicatorSections,
  comparisonCounty, comparisonData,
  selectedYear, onYearSelect,
  goals = {},
  onIndicatorSelect
}) {
  const config = useDashboardConfig();

  // Use sections if provided, otherwise fall back to a single ungrouped section
  const sections = indicatorSections ?? [{ theme: null, indicators: enabledIndicators ?? config.indicators }];
  // Flat ordered list of indicators (respects theme grouping order)
  const indicators = sections.flatMap(s => s.indicators);

  const hasThemes = sections.some(s => s.theme !== null);

  const themeStartIds = new Set(
    sections.slice(1).map(s => s.indicators[0]?.id).filter(Boolean)
  );

  const parts = config.org.geography.label.split(',');
  const countyLabel = parts[0]?.trim() ?? config.org.geography.label;
  const stateLabel = parts[1] ? `${parts[1].trim()} Statewide` : 'Statewide';

  // ── Proportional row heights ──────────────────────────────────────────────
  const wrapRef = useRef(null);
  const theadRef = useRef(null);
  const [rowUnit, setRowUnit] = useState(0);

  const hasGoal = indicators.some(ind => typeof goals[ind.id] === 'number');
  const indicatorRowCount = 2
    + (hasGoal ? 1 : 0)
    + (comparisonCounty ? 1 : 0)
    + (selectedTract ? 1 : 0);
  const totalUnits = indicatorRowCount + 2; // trend row counts as 2

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const thead = theadRef.current;
    if (!wrap || !thead) return;
    const measure = () => {
      const available = wrap.getBoundingClientRect().height - thead.getBoundingClientRect().height;
      setRowUnit(Math.max(available / totalUnits, 0));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [totalUnits]);

  const rowStyle = rowUnit > 0 ? { height: rowUnit } : undefined;
  const trendStyle = rowUnit > 0 ? { height: rowUnit * 2 } : undefined;

  // For each indicator, is the selected year actually present in fetched data?
  // null selectedYear = Latest Available — always has data.
  const allDataSafe = useMemo(() => allData || new Map(), [allData]);

  const yearAvailability = useMemo(() => {
    if (!selectedYear) return null;
    return new Map(indicators.map(ind => [
      ind.id,
      !!(allDataSafe.get(ind.id)?.trend?.some(p => p.year === selectedYear))
    ]));
  }, [selectedYear, indicators, allDataSafe]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="scorecard-table-wrap" ref={wrapRef}>
      <table className="scorecard-table">
        <thead ref={theadRef}>
          {hasThemes && (
            <tr className="scorecard-theme-row">
              <th rowSpan={2} className="scorecard-th-geo">
                Geography
                {selectedYear && (
                  <span className="scorecard-year-badge">{selectedYear}</span>
                )}
              </th>
              {sections.map(section => (
                <th
                  key={section.theme ?? '__ungrouped__'}
                  colSpan={section.indicators.length}
                  className={section.theme ? 'scorecard-theme-header' : 'scorecard-theme-header scorecard-theme-header--empty'}
                >
                  {section.theme ?? ''}
                </th>
              ))}
            </tr>
          )}
          <tr>
            {!hasThemes && (
              <th className="scorecard-th-geo">
                Geography
                {selectedYear && (
                  <span className="scorecard-year-badge">{selectedYear}</span>
                )}
              </th>
            )}
            {indicators.map(ind => (
              <th key={ind.id} className={themeStartIds.has(ind.id) ? 'scorecard-theme-start' : undefined}>
                {ind.label}
                {onIndicatorSelect && (
                  <button
                    className="scorecard-col-nav"
                    onClick={e => { e.stopPropagation(); onIndicatorSelect(ind.id); }}
                    title={`Open ${ind.label} map`}
                  >↗</button>
                )}
                <span className="scorecard-col-meta">
                  {ind.source === 'census' ? 'ACS'
                    : ind.source === 'cdc' ? 'CDC'
                    : ind.source === 'svi' ? 'SVI'
                    : ind.source === 'ejscreen' ? 'EJS'
                    : ind.source === 'education' ? 'NCES'
                    : (ind.source?.toUpperCase() ?? '')}
                  {(selectedYear ?? ind.years?.[ind.years.length - 1]) && ` · ${selectedYear ?? ind.years[ind.years.length - 1]}`}
                </span>
                {yearAvailability?.get(ind.id) === false && (
                  <span className="scorecard-col-warn">no data for {selectedYear}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Home county — reference row, no delta */}
          <tr className="scorecard-row--community" style={rowStyle}>
            <td>{countyLabel}</td>
            {indicators.map(ind => {
              const result = allDataSafe.get(ind.id);
              const val = selectedYear
                ? getYearValue(result?.trend, selectedYear)
                : result?.countyValue ?? null;
              return <td key={ind.id} className={themeStartIds.has(ind.id) ? 'scorecard-theme-start' : undefined}>{formatValue(val, ind.unit)}</td>;
            })}
          </tr>

          {/* Goal row — shown when at least one indicator has a goal */}
          {indicators.some(ind => typeof goals[ind.id] === 'number') && (
            <tr className="scorecard-row--goal" style={rowStyle}>
              <td>Goal</td>
              {indicators.map(ind => {
                const goal = goals[ind.id];
                return (
                  <td key={ind.id} className={themeStartIds.has(ind.id) ? 'scorecard-theme-start' : undefined}>
                    {typeof goal === 'number' && !isNaN(goal)
                      ? formatValue(goal, ind.unit)
                      : <span className="scorecard-goal-empty">—</span>
                    }
                  </td>
                );
              })}
            </tr>
          )}

          {/* State */}
          <tr className="scorecard-row--state" style={rowStyle}>
            <td>{stateLabel}</td>
            {indicators.map(ind => {
              const result = allDataSafe.get(ind.id);
              const countyRef = selectedYear
                ? getYearValue(result?.trend, selectedYear)
                : result?.countyValue ?? null;
              const val = selectedYear
                ? getYearValue(result?.stateTrend, selectedYear)
                : result?.stateValue ?? null;
              return (
                <td key={ind.id} className={themeStartIds.has(ind.id) ? 'scorecard-theme-start' : undefined}>
                  {formatValue(val, ind.unit)}
                  <DeltaBadge value={val} reference={countyRef} unit={ind.unit} />
                </td>
              );
            })}
          </tr>

          {/* Comparison county */}
          {comparisonCounty && comparisonData && (
            <tr className="scorecard-row--comparison" style={rowStyle}>
              <td>{comparisonCounty.name}</td>
              {indicators.map(ind => {
                const result = allDataSafe.get(ind.id);
                const compResult = comparisonData.get(ind.id);
                const countyRef = selectedYear
                  ? getYearValue(result?.trend, selectedYear)
                  : result?.countyValue ?? null;
                const val = selectedYear
                  ? getYearValue(compResult?.trend, selectedYear)
                  : compResult?.countyValue ?? null;
                return (
                  <td key={ind.id} className={themeStartIds.has(ind.id) ? 'scorecard-theme-start' : undefined}>
                    {formatValue(val, ind.unit)}
                    <DeltaBadge value={val} reference={countyRef} unit={ind.unit} />
                  </td>
                );
              })}
            </tr>
          )}

          {/* Selected tract */}
          {selectedTract && (
            <tr className="scorecard-row--tract" style={rowStyle}>
              <td>
                {selectedTract.name ?? selectedTract.geoid}
                <span className="scorecard-row-note">latest yr.</span>
              </td>
              {indicators.map(ind => {
                const result = allDataSafe.get(ind.id);
                const tractRow = result?.geography?.find(g => g.geoid === selectedTract.geoid);
                const val = tractRow?.value ?? null;
                const countyRef = result?.countyValue ?? null;
                return (
                  <td key={ind.id} className={themeStartIds.has(ind.id) ? 'scorecard-theme-start' : undefined}>
                    {formatValue(val, ind.unit)}
                    <DeltaBadge value={val} reference={countyRef} unit={ind.unit} />
                  </td>
                );
              })}
            </tr>
          )}

          {/* Trend sparklines */}
          <tr className="scorecard-row--trend" style={trendStyle}>
            <td className="scorecard-trend-label">
              <span>Trend</span>
              <span className="scorecard-trend-sublabel">Home county</span>
              <span className="scorecard-trend-sublabel">Click year to filter</span>
            </td>
            {indicators.map(ind => {
              const result = allDataSafe.get(ind.id);
              const compResult = comparisonData?.get(ind.id);
              return (
                <td key={ind.id} className={`scorecard-trend-cell${themeStartIds.has(ind.id) ? ' scorecard-theme-start' : ''}`}>
                  <IndicatorSparkline
                    trend={result?.trend}
                    stateTrend={result?.stateTrend}
                    comparisonTrend={compResult?.trend}
                    comparisonName={comparisonCounty?.name}
                    selectedYear={selectedYear}
                    onYearSelect={onYearSelect}
                    primaryColor={config.branding.primaryColor}
                    accentColor={config.branding.accentColor}
                    unit={ind.unit}
                    fontBody={config.branding.fontBody}
                  />
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
